import React, { useEffect, useState } from 'react';
import { Download, Trash2, Clock, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';
import { translationApi, TranslationTask } from '../../services/translationApi';
import Button from '../common/Button';
import ConfirmModal from '../common/ConfirmModal';
import { getUrlWithToken } from '../../utils/urlUtils';
import './TranslationHistory.css';

interface Props {
    onRefreshTrigger?: number; // Prop to trigger external refresh
}

const TranslationHistory: React.FC<Props> = ({ onRefreshTrigger = 0 }) => {
    const [tasks, setTasks] = useState<TranslationTask[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 10;

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState<string | null>(null);

    const fetchTasks = async () => {
        setIsLoading(true);
        try {
            const resp = await translationApi.getTasks(page, pageSize);
            setTasks(resp.items);
            setTotal(resp.total);
        } catch (error) {
            console.error('Failed to fetch translation history:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, onRefreshTrigger]);

    const confirmDelete = (taskId: string) => {
        setTaskToDelete(taskId);
        setIsDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        if (!taskToDelete) return;

        try {
            await translationApi.deleteTask(taskToDelete);
            setIsDeleteModalOpen(false);
            setTaskToDelete(null);

            if (tasks.length === 1 && page > 1) {
                setPage(p => p - 1); // useEffect on page change will trigger fetchTasks
            } else {
                fetchTasks();
            }
        } catch (error) {
            console.error('Failed to delete task:', error);
            alert('删除失败，请稍后重试');
            setIsDeleteModalOpen(false);
            setTaskToDelete(null);
        }
    };

    const cancelDelete = () => {
        setIsDeleteModalOpen(false);
        setTaskToDelete(null);
    };

    const handleDownload = (url?: string) => {
        if (!url) return;
        const token = localStorage.getItem('auth_token');
        const finalUrl = getUrlWithToken(url, token);
        const a = document.createElement('a');
        a.href = finalUrl;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleString();
    };

    if (tasks.length === 0 && !isLoading) {
        return (
            <div className="translation-history empty">
                <p>暂无翻译记录</p>
            </div>
        );
    }

    return (
        <div className="translation-history">
            <div className="history-header">
                <h3>翻译历史</h3>
                <button className="refresh-btn" onClick={fetchTasks} disabled={isLoading}>
                    <RefreshCw size={16} className={isLoading ? 'spinning' : ''} />
                </button>
            </div>

            <div className="table-responsive">
                <table className="history-table">
                    <thead>
                        <tr>
                            <th>文件名称</th>
                            <th>目标语言</th>
                            <th>状态</th>
                            <th>时间</th>
                            <th>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasks.map(task => (
                            <tr key={task.task_id}>
                                <td className="filename-cell" title={task.source_filename}>
                                    {task.source_filename}
                                </td>
                                <td>{task.target_language}</td>
                                <td>
                                    <span className={`status-badge status-${task.status}`}>
                                        {task.status === 'completed' && <CheckCircle2 size={14} />}
                                        {task.status === 'failed' && <AlertCircle size={14} />}
                                        {(task.status === 'pending' || task.status === 'translating') && <Clock size={14} />}
                                        <span>
                                            {task.status === 'completed' ? '已完成' :
                                                task.status === 'failed' ? '失败' :
                                                    task.status === 'translating' ? '翻译中' :
                                                        task.status === 'pending' ? '等待处理' : task.status}
                                        </span>
                                    </span>
                                </td>
                                <td className="date-cell">{formatDate(task.created_at)}</td>
                                <td className="actions-cell">
                                    {task.status === 'completed' && task.download_url && (
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleDownload(task.download_url)}
                                            title="下载译文"
                                        >
                                            <Download size={14} />
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="delete-btn"
                                        onClick={() => confirmDelete(task.task_id)}
                                        title="删除记录"
                                    >
                                        <Trash2 size={14} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {total > pageSize && (
                <div className="pagination">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        上一页
                    </Button>
                    <span className="page-info">{page} / {Math.ceil(total / pageSize)}</span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= Math.ceil(total / pageSize)}
                        onClick={() => setPage(p => p + 1)}
                    >
                        下一页
                    </Button>
                </div>
            )}

            <ConfirmModal
                isOpen={isDeleteModalOpen}
                title="删除记录"
                message="确定要删除这条翻译记录吗？此操作也会彻底删除相关的原文和译文文件，且不可恢复。"
                confirmText="确定"
                cancelText="取消"
                onConfirm={handleDelete}
                onCancel={cancelDelete}
                isDestructive={true}
            />
        </div>
    );
};

export default TranslationHistory;
